<script>
    import { createEventDispatcher } from 'svelte';
	import { navigate } from 'svelte-routing';

    import { vptServer, token, inmateCount, filterShowTargetedInmatesOnly, getTooltipDisplayOptions } from '../../stores.js';
    import { hydrateInmate, getInmateSearchModel, getInmateSurveyModel, getInmateContactModel, getInmateDetailsTooltip,
        getTemporaryDischargeTooltip, getCircumstanceDateTooltip, getArrivalTooltip, ModalTypeEnum } from '../../inmate.js'
    import { getTriggerDatesTooltip, isDateInTriggerDates } from '../../trigger-date.js';
    import { getSurveyScheduleTooltip, getSurveyToastProperties } from "../../survey.js";
    import { getContactWarningTooltip } from '../../contact.js';
    import { formatDate, isToday, getIsoDateStringAsDate, getDaysDifference } from '../../date-time.js';
    import { createRequestParams, isMatching } from "../../string.js";
    import { referenceDataEthnicityList, referenceDataRegimeLevelList, referenceDataCellStatusList } from "../../reference-data.js";
    import { config, countActiveEarlyDaysElements } from '../../config.js';
    import { sortByProperty } from '../../sort.js';

    import AimToast from '../../component/AimToast.svelte';
    import Dropdown from '../../component/Dropdown.svelte';
    import FilterButton from '../../component/FilterButton.svelte';

    import AddContactModal from '../../modal/AddContactModal.svelte';
    import ContactModal from '../../modal/ContactModal.svelte';
    import SurveyModal from '../../modal/SurveyModal.svelte';

    import TableHead from '../../table/TableHead.svelte';
    import TableData from '../../table/TableData.svelte';
    import TableAction from '../../table/TableAction.svelte';
    import TargetedData from '../../table/TargetedData.svelte';
    import InmateData from '../../table/InmateData.svelte';
    import LocationData from '../../table/LocationData.svelte';
    import EngagementData from '../../table/EngagementData.svelte';

    export let requestProperties;

    const dispatch = createEventDispatcher();

    let inmates = [];
    let filteredInmates = [];
    let sites = [];
    let searchCompleted = false;
    let sortProperty = "rowOrdering";
    let reverseSorted = false;

    let toastProperties = null; 
    
    // Table filters and controls
    let filterShowNewInmatesOnly = false;
    let filterShowRemandedOnly = false;
    let filterShowNonDischargedOnly = false;
    let filterShowTriggerDatesOnly = false;
    let filterShowOverdueSurveysOnly = false;
    let filterShowWarningContactsOnly = false;
    let filterSiteName = "";
    let filterFreeText = "";
    let controlShowRecentChanges = false;

    // Modal related variables
    let selectedInmate = null;
    let selectedModalType = null;

    // Automatically update filtered inmates list when sort property or filters are changed.
    $: {
        inmates.sort(sortByProperty(sortProperty, reverseSorted));
 
        filteredInmates = inmates.filter(function(inmate) {
            if ($filterShowTargetedInmatesOnly && !inmate.indicatorTargeted) {
                return false; }
            if (filterShowNewInmatesOnly && !inmate.indicatorNewInmate) {
                return false; }
            if (filterShowRemandedOnly && !inmate.indicatorRemand) {
                return false; }
            if (filterShowNonDischargedOnly && inmate.dateTemporaryDischarged) {
                return false; }
            if (filterShowTriggerDatesOnly && !(inmate.triggerDateList && inmate.triggerDateList.length > 0)) {
                return false; }
            if (filterShowOverdueSurveysOnly && !inmate.surveyOverdue) {
                return false; }
            if (filterShowWarningContactsOnly && !inmate.contactWarning) { 
                return false; }
            if ($config.showSiteName && filterSiteName != "" && !(filterSiteName == inmate.siteName)) {
                return false; }
            if (filterFreeText != "" && !isMatching(getInmateSearchModel(inmate), filterFreeText.trim())) {
                return false; }
            return true;
        });
    }

    $: {
        if (requestProperties && requestProperties.requestDate) {
            searchCompleted = false;
            getDailyReport(requestProperties.requestDate + createRequestParams(requestProperties.requestParams));
        }
    }

    $: keyworkerExists = inmates.filter(i => i.keyworkerName).length > 0;

    $: numberOfActiveElements = countActiveEarlyDaysElements($config);

    async function getDailyReport(requestString) {
        await fetch(`${$vptServer}/establishment/earlydays/list/${requestString}`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            data.forEach((i,index,arr) => {
                arr[index] = {...i,
                    ...hydrateInmate(i, i.indicatorTrend, $referenceDataEthnicityList, $referenceDataRegimeLevelList, $referenceDataCellStatusList, requestProperties.requestDate, $config.numberOfDaysConsideredNewCircumstance),
                    daysSinceEngagement: (i.dateLastEngagement && i.dateLastEngagement != null) ? getDaysDifference(getIsoDateStringAsDate(i.dateLastEngagement), new Date()) : null};
        });
            inmates = data;
        })
        .catch(error => {
            console.error("Error getting early days list");
            console.error(error);
            return;
        });

        if ($config.showSiteName) {
            sites = [];
            filteredInmates.forEach((inmate) => {
                if (inmate.siteUrn && !(sites.some(e => e.label === inmate.siteName))) {
                    let site = {value: inmate.siteUrn, label: inmate.siteName}
                    sites = [...sites, site];
                }
            });
            sites = sites.sort(sortByProperty("label", false));
        }

        searchCompleted = true;
    }

    function sort(newSortProperty, reverse) {
        if (newSortProperty === sortProperty) {
            reverseSorted = !reverseSorted;
        }
        else {
            sortProperty = newSortProperty;
            reverseSorted = reverse ? true : false;
        }
    }

    function openModal(inmate, modalType) {
        selectedModalType = modalType;
        if (modalType === ModalTypeEnum.SURVEY) {
            selectedInmate = getInmateSurveyModel(inmate);
        } else if (modalType === ModalTypeEnum.ADD_CONTACT) {
            selectedInmate = { inmateUrn: inmate.inmateUrn, inmateName: inmate.inmateName, unseenDays : inmate.contactTargetedUnseenDays };
        } else if (modalType === ModalTypeEnum.CONTACT) {
            selectedInmate = getInmateContactModel(inmate);
        }
    }

    function closeModal(event) {
        if (event.detail.scheduleType != null) {
            toastProperties = getSurveyToastProperties(event.detail.scheduleType, event.detail.surveyScheduleTotal, selectedInmate.inmateName, selectedInmate.inmateUrn);
        }

        selectedInmate = null;
        selectedModalType = null;
        
        if (event.detail.refresh) {
            if (requestProperties && requestProperties.requestDate) {
                searchCompleted = false;
                getDailyReport(requestProperties.requestDate + createRequestParams(requestProperties.requestParams));
            }
        }
    }
</script>

<div class="container-fluid">
    {#if searchCompleted && inmates.length > 0}
        <div class="row table-filters">
            <div class="col-sm-12">
                <div class="form-inline" role="toolbar" aria-label="Table filters">
                    <div>
                        <FilterButton on:click={() => {filterShowTargetedInmatesOnly.set(!($filterShowTargetedInmatesOnly))}} id="filter-targeted"
                                isActive={$filterShowTargetedInmatesOnly} classes={"mr-3 btn-img"}
                                title="Show only Targeted {$config.inmateLabelName.toLowerCase()}s on the {$config.earlyDaysLabelName} aspect">
                            <img src="/assets/filter/target{$filterShowTargetedInmatesOnly ? '' : '-inverse'}.svg" alt={"Show only Targeted"}/>
                        </FilterButton>

                        <FilterButton on:click={() => {filterShowTriggerDatesOnly = !filterShowTriggerDatesOnly}} id="filter-trigger-dates"
                            isActive={filterShowTriggerDatesOnly} icon={"pin-angle-fill"} classes={"mr-1"}
                            title="Show only {$config.inmateLabelName.toLowerCase()}s with recent or upcoming {$config.triggerDatesLabelName}" />

                        {#if $config.numberOfDaysToShowTemporaryDischarge}
                            <FilterButton on:click={() => {filterShowNonDischargedOnly = !filterShowNonDischargedOnly}} id="filter-non-discharged"
                                isActive={filterShowNonDischargedOnly} icon={"building-fill"} classes={"mr-1"}
                                title="Show only {$config.inmateLabelName.toLowerCase()}s who are currently on the premises" />
                        {/if}

                        <FilterButton on:click={() => {filterShowRemandedOnly = !filterShowRemandedOnly}} id="filter-remand"
                            isActive={filterShowRemandedOnly} icon={"hourglass-split"} classes={"mr-1"}
                            title="Show only {$config.inmateLabelName.toLowerCase()}s on Remand" />

                        <FilterButton on:click={() => {filterShowNewInmatesOnly = !filterShowNewInmatesOnly}} id="filter-new-inmates"
                            isActive={filterShowNewInmatesOnly} icon={"calendar4-week"} classes={"mr-3"}
                            title="Show only {$config.inmateLabelName.toLowerCase()}s who arrived in the last {$config.numberOfDaysConsideredNewInmate} day{$config.numberOfDaysConsideredNewInmate == 1 ? '' : 's'}" />
                        
                        {#if $config.contactTypeId}
                            <FilterButton on:click={() => {filterShowWarningContactsOnly = !filterShowWarningContactsOnly}} id="filter-warning-contacts"
                                isActive={filterShowWarningContactsOnly} icon={"sticky"} alertIcon={"exclamation-circle"} classes={$config.surveyUrn ? "mr-1" : "mr-3"}
                                title="Show only {$config.inmateLabelName.toLowerCase()}s where Contact is suggested" />    
                        {/if}

                        {#if $config.surveyUrn}
                            <FilterButton on:click={() => {filterShowOverdueSurveysOnly = !filterShowOverdueSurveysOnly}} id="filter-survey-overdue"
                                isActive={filterShowOverdueSurveysOnly} icon={"clipboard-data"} alertIcon={"clock"} classes={"mr-3"}
                                title="Show only {$config.inmateLabelName.toLowerCase()}s whose {$config.surveyLabelName} Survey is overdue" />
                        {/if}

                        <FilterButton on:click={()=> {controlShowRecentChanges = !controlShowRecentChanges}} id="control-recent-changes"
                            isActive={controlShowRecentChanges} icon={"shuffle"} title={"Highlight recent circumstance changes"} />
                    </div>

                    <div>
                        {#if $config.showSiteName && sites.length > 1}
                            <Dropdown bind:value={filterSiteName} id={"sites"} options={sites} allLabel={"All Sites"} classes="mr-3"/>
                        {/if}

                        <label for="search" class="sr-only">Free Text Search</label>
                        <input type="text" class="form-control" id="search" bind:value={filterFreeText} placeholder="Search..." />
                    </div>
                </div>
            </div>
        </div>
    {/if}

    <div class="row">
        <div class="col-sm-12">
            {#if searchCompleted}
                {#if filteredInmates.length > 0 && !($inmateCount.filtered > $inmateCount.unfiltered)}
                    <table class="table table-sm table-borderless table-hover table-card">
                        <thead>
                            <tr>
                                <TableHead on:click={() => sort("rowOrdering")} propertyName={"rowOrdering"} width={'xs'}
                                        tooltipId={"target-header"} tooltipText={`${$config.inmateLabelName} is on the Targeted list(s)`} 
                                        {sortProperty} {reverseSorted} invertArrow={true}>
                                    <img id="target-header" src="/assets/header/target.svg" alt="{$config.inmateLabelName} is on the Targeted list(s)" />
                                </TableHead>

                                <TableHead on:click={() => sort("trendSort")} propertyName={"trendSort"} icon={"graph-up"}
                                    tooltipText={"Daily Change"} tooltipId={"trend-header"} 
                                    {sortProperty} {reverseSorted} invertArrow={true} />

                                <TableHead on:click={() => sort("inmateName")} propertyName={"inmateName"} title={$config.inmateLabelName}
                                    {sortProperty} {reverseSorted} />

                                <TableHead on:click={() => sort("triggerDateSort", true)} propertyName={"triggerDateSort"} icon={"pin-angle-fill"}
                                    tooltipText={`Recent / Upcoming ${$config.triggerDatesLabelName}`} tooltipId={"trigger-date-header"} 
                                    {sortProperty} {reverseSorted} />

                                <TableHead on:click={() => sort("cellStatusSort")} propertyName={"cellStatusSort"} icon={"file-person"}
                                    tooltipText="Cell Status" tooltipId={"cell-status-header"}
                                    {sortProperty} {reverseSorted} invertArrow={true} />

                                <TableHead on:click={() => sort("wingCellSort")} propertyName={"wingCellSort"} title={"Cell"}
                                    {sortProperty} {reverseSorted} />
                                
                                <TableHead on:click={() => sort("daysSinceTemporaryDischarge", true)} propertyName={"daysSinceTemporaryDischarge"} 
                                    icon={"building-fill"} tooltipText={"Temporary Discharge"} tooltipId={"temp-discharge-header"} 
                                    {sortProperty} {reverseSorted} />

                                {#if $config.earlyDaysVisit}
                                    <TableHead on:click={() => sort("countVisit")} propertyName={"countVisit"} width={'md'} center={true}
                                        title={$config.isolationVisitLabel} tooltipId={"visits-header"} {sortProperty} {reverseSorted} invertArrow={true}
                                        tooltipText={($config.isolationVisitDescription && !($config.isolationVisitLabel == $config.isolationVisitDescription)) ? $config.isolationVisitLabel : ""} />
                                {/if}

                                {#if $config.earlyDaysWork}
                                    <TableHead on:click={() => sort("countWork")} propertyName={"countWork"} width={'md'} center={true}
                                        title={$config.isolationWorkLabel} tooltipId={"work-header"} {sortProperty} {reverseSorted} invertArrow={true}
                                        tooltipText={($config.isolationWorkDescription && !($config.isolationWorkLabel == $config.isolationWorkDescription)) ? $config.isolationWorkDescription : ""} />
                                {/if}

                                {#if $config.earlyDaysPhone}
                                    <TableHead on:click={() => sort("countPhone")} propertyName={"countPhone"} width={'md'} center={true}
                                        title={$config.isolationPhoneLabel} tooltipId={"phone-header"} {sortProperty} {reverseSorted} invertArrow={true}
                                        tooltipText={($config.isolationPhoneDescription && !($config.isolationPhoneLabel == $config.isolationPhoneDescription)) ? $config.isolationPhoneDescription : ""} />
                                {/if}

                                {#if $config.earlyDaysMeal}
                                    <TableHead on:click={() => sort("countMeal")} propertyName={"countMeal"} width={'md'} center={true}
                                        title={$config.isolationMealLabel} tooltipId={"meals-header"} {sortProperty} {reverseSorted} invertArrow={true}
                                        tooltipText={($config.isolationMealDescription && !($config.isolationMealLabel == $config.isolationMealDescription)) ? $config.isolationMealDescription : ""} />
                                {/if}

                                {#if $config.earlyDaysCanteen}
                                    <TableHead on:click={() => sort("countCanteen")} propertyName={"countCanteen"} width={'md'} center={true}
                                        title={$config.isolationCanteenLabel} tooltipId={"canteen-header"} {sortProperty} {reverseSorted} invertArrow={true}
                                        tooltipText={($config.isolationCanteenDescription && !($config.isolationCanteenLabel == $config.isolationCanteenDescription)) ? $config.isolationCanteenDescription : ""} />
                                {/if}

                                {#if $config.earlyDaysGym}
                                    <TableHead on:click={() => sort("countGym")} propertyName={"countGym"} width={'md'} center={true}
                                        title={$config.isolationGymLabel} tooltipId={"gym-header"} {sortProperty} {reverseSorted} invertArrow={true}
                                        tooltipText={($config.isolationGymDescription && !($config.isolationGymLabel == $config.isolationGymDescription)) ? $config.isolationGymDescription : ""} />
                                {/if}

                                {#if $config.earlyDaysPa}
                                    <TableHead on:click={() => sort("countActivity")} propertyName={"countActivity"} width={'md'} center={true}
                                        title={$config.isolationPaLabel} tooltipId={"activities-header"} {sortProperty} {reverseSorted} invertArrow={true}
                                        tooltipText={($config.isolationPaDescription && !($config.isolationPaLabel == $config.isolationPaDescription)) ? $config.isolationPaDescription : ""} />
                                {/if}

                                <TableHead on:click={() => sort("daysSinceEngagement")} propertyName={"daysSinceEngagement"} 
                                    title={"Engaged"} tooltipText="Last Engaged" tooltipId={"engaged-header"} 
                                    {sortProperty} {reverseSorted} classes="table-pad-left"/>

                                <TableHead on:click={() => sort("daysSinceArrival")} propertyName={"daysSinceArrival"}
                                    title={"Arrived"} {sortProperty} {reverseSorted} />

                                <TableHead on:click={() => sort("regimeLevelDescription")} propertyName={"regimeLevelDescription"} 
                                    title={"Regime"} {sortProperty} {reverseSorted} classes="hidden-col-lv-3" />

                                <TableHead on:click={() => sort("cashBalance")} propertyName={"cashBalance"} title={"Balance"} 
                                    {sortProperty} {reverseSorted} classes="hidden-col-lv-3" />

                                <TableHead on:click={() => sort("indicatorRemand", true)} propertyName={"indicatorRemand"} icon={"hourglass-split"}
                                    tooltipText={`${$config.inmateLabelName} is on remand`} tooltipId={"remand-header"} 
                                    {sortProperty} {reverseSorted} />

                                <TableHead on:click={() => sort("status")} propertyName={"status"} 
                                    title={"Status"} {sortProperty} {reverseSorted} classes="hidden-col-lv-2" />

                                {#if keyworkerExists}
                                    <TableHead on:click={() => sort("keyworkerName")} propertyName={"keyworkerName"} 
                                        title={$config.keyworkerLabelName} {sortProperty} {reverseSorted} />
                                {/if}

                                {#if $config.showSiteName && sites.length > 1}
                                    <TableHead on:click={() => sort("siteName")} propertyName={"siteName"}
                                        title={"Site"} {sortProperty} {reverseSorted} classes="hidden-col-lv-1" />
                                {/if}

                                {#if $config.contactTypeId && requestProperties && requestProperties.requestDate && isToday(getIsoDateStringAsDate(requestProperties.requestDate))}
                                    <TableHead on:click={() => sort("contactSort", true)} propertyName={"contactSort"}
                                        icon="sticky" tooltipText="Contact" tooltipId={"contact-header"}
                                        {sortProperty} {reverseSorted} action={true} />
                                {/if}

                                {#if $config.surveyUrn && requestProperties && requestProperties.requestDate && isToday(getIsoDateStringAsDate(requestProperties.requestDate))}
                                    <TableHead on:click={() => sort("surveyScheduleNextSurveyDate")} propertyName={"surveyScheduleNextSurveyDate"}
                                        icon="clipboard-data" tooltipText="{$config.surveyLabelName} Survey Schedule" tooltipId={"survey-header"}
                                        {sortProperty} {reverseSorted} invertArrow={true} action={true} />
                                {/if}
                            </tr>
                        </thead>

                        <tbody>
                            {#each filteredInmates as inmate (inmate.inmateUrn)}
                                <tr on:click={() => {navigate(`/history/${inmate.inmateUrn}`)}}>
                                    
                                    <!-- Targeted Indicator -->                          
                                    <TargetedData inmateUrn={inmate.inmateUrn} targeted={inmate.indicatorTargeted} 
                                        targetedTotal={inmate.targetedTotal} inmateLabel={$config.inmateLabelName} />

                                    <!-- Trend Indicator -->
                                    <TableData display={inmate.indicatorTrend == 'U'} icon={"arrow-up-short"} iconSize={'xl'}
                                        tooltipText={`${$config.inmateLabelName}'s engagement has increased compared to ${isToday(getIsoDateStringAsDate(requestProperties.requestDate)) ? 'yesterday' :' the previous day'}`} 
                                        tooltipId={`trend-indicator-${inmate.inmateUrn}`} classes={"text-success"}/>

                                    <!-- Inmate -->
                                    <InmateData inmateUrn={inmate.inmateUrn} name={inmate.inmateName} 
                                        tooltipText={getInmateDetailsTooltip(inmate)} tooltipOptions={getTooltipDisplayOptions({ position: "right" })} />
                                
                                    <!-- Trigger Date Indicator -->
                                    <TableData display={inmate.triggerDateList && inmate.triggerDateList.length > 0} icon={"pin-angle-fill"}
                                        aimTooltip={true} tooltipOptions={getTooltipDisplayOptions({ align: "left", maxWidth: 500 })}
                                        tooltipText={getTriggerDatesTooltip(inmate.triggerDateList)}
                                        classes={isDateInTriggerDates(requestProperties.requestDate, inmate.triggerDateList) ? "text-danger" : ""} />

                                    <!-- Cell Status -->
                                    {#if $config.showCellStatusIcon}
                                        <TableData display={inmate.vptCellStatusUrn > 0 && inmate.cellStatusIcon} center={true} 
                                                tooltipId={`cell-status-data-${inmate.inmateUrn}`} tooltipText={inmate.cellStatusDescription}>
                                            <img id="cell-status-data-{inmate.inmateUrn}" src="/assets/{inmate.cellStatusIcon}.svg" alt="{inmate.cellStatusDescription}" />
                                        </TableData>
                                    {/if}

                                    <!-- Cell Location -->
                                    <LocationData inmateUrn={inmate.inmateUrn} unit={inmate.unit} wing={inmate.wing} cell={inmate.cell} 
                                        cellStartDate={inmate.cellStartDate} cellFinishDate={inmate.cellFinishDate} 
                                        highlight={controlShowRecentChanges && inmate.isRecentCellChange} />

                                    <!-- Temporary Discharge Indicator -->
                                    <TableData display={inmate.daysSinceTemporaryDischarge > 0} icon={"building-fill-x"}
                                        aimTooltip={true} tooltipOptions={getTooltipDisplayOptions({ maxWidth: 500 })}
                                        tooltipText={getTemporaryDischargeTooltip(inmate.daysSinceTemporaryDischarge, inmate.dischargedTo)} />

                                    <!-- Display Early Days engagement unless the inmate is temporarily discharged -->
                                    {#if !$config.numberOfDaysToShowTemporaryDischarge && !inmate.daysSinceTemporaryDischarge || (inmate.daysSinceTemporaryDischarge < $config.numberOfDaysToShowTemporaryDischarge)}

                                        <!-- Visits Engagement -->
                                        {#if $config.earlyDaysVisit}
                                            <EngagementData activityNamePlural={"Visits"} activityNamePastEngagement={"Visited"}
                                                engagementCount={inmate.countVisit} tooltipId={`visit-data-${inmate.inmateUrn}`} />
                                        {/if}

                                        <!-- Work Engagement -->
                                        {#if $config.earlyDaysWork}
                                            <EngagementData activityNamePlural={"Attendances"} activityNamePastEngagement={"Attended"}
                                                engagementCount={inmate.countWork} tooltipId={`work-data-${inmate.inmateUrn}`} />
                                        {/if}

                                        <!-- Phone Engagement -->
                                        {#if $config.earlyDaysPhone}
                                            <EngagementData activityNamePlural={"Top Ups"} activityNamePastEngagement={"Topped Up"}
                                                engagementCount={inmate.countPhone} tooltipId={`phone-data-${inmate.inmateUrn}`} />
                                        {/if}

                                        <!-- Meals Engagement -->
                                        {#if $config.earlyDaysMeal}
                                            <EngagementData activityNamePlural={"Selections"} activityNamePastEngagement={"Selected"}
                                                engagementCount={inmate.countMeal} tooltipId={`meal-data-${inmate.inmateUrn}`} />
                                        {/if}

                                        <!-- Canteen Engagement -->
                                        {#if $config.earlyDaysCanteen}
                                            <EngagementData activityNamePlural={"Purchases"} activityNamePastEngagement={"Purchased"}
                                                engagementCount={inmate.countCanteen} tooltipId={`canteen-data-${inmate.inmateUrn}`} />
                                        {/if}

                                        <!-- Gym Engagement -->
                                        {#if $config.earlyDaysGym}
                                            <EngagementData activityNamePlural={"Attendances"} activityNamePastEngagement={"Attended"}
                                                engagementCount={inmate.countGym} tooltipId={`gym-data-${inmate.inmateUrn}`} />
                                        {/if}

                                        <!-- PA Engagement -->
                                        {#if $config.earlyDaysPa}
                                            <EngagementData activityNamePlural={"Attendances"} activityNamePastEngagement={"Attended"}
                                                engagementCount={inmate.countActivity} tooltipId={`pa-data-${inmate.inmateUrn}`} />
                                        {/if}
                                    {:else}
                                        <td class="table-text-well" colspan="{numberOfActiveElements}">
                                            <span class="table-text-center">
                                                {$config.inmateLabelName} has been temporarily discharged for {inmate.daysSinceTemporaryDischarge} days
                                            </span>
                                        </td>
                                    {/if}

                                    <!-- Engaged -->
                                    <TableData display={inmate.dateLastEngagement} text={inmate.labelLastEngagement} classes="table-pad-left"
                                        tooltipId={`engaged-data-${inmate.inmateUrn}`} tooltipText={formatDate(inmate.dateLastEngagement)} />

                                    <!-- Arrived -->
                                    <TableData tooltipId={`arrival-data-${inmate.inmateUrn}`}
                                            tooltipText={getArrivalTooltip(inmate.dateArrived, inmate.indicatorNewInmate, inmate.indicatorEarlyDays)}>
                                        <span id="arrival-data-{inmate.inmateUrn}" 
                                                class:badge={inmate.indicatorNewInmate || inmate.indicatorEarlyDays}
                                                class:badge-arrival={inmate.indicatorNewInmate || inmate.indicatorEarlyDays}
                                                class:badge-danger={inmate.indicatorNewInmate}
                                                class:badge-border={inmate.indicatorNewInmate}
                                                class:badge-primary={!inmate.indicatorNewInmate && inmate.indicatorEarlyDays}>
                                            {inmate.timeSinceArrival}
                                        </span>
                                    </TableData>

                                    <!-- Regime -->
                                    <TableData display={inmate.regimeLevelDescription} text={inmate.regimeLevelDescription} tooltipId={`regime-data-${inmate.inmateUrn}`}
                                        tooltipText={getCircumstanceDateTooltip(inmate.regimeStartDate, inmate.regimeFinishDate)} 
                                        highlight={controlShowRecentChanges && inmate.isRecentRegimeChange} classes="hidden-col-lv-3" />

                                    <!-- Balance -->
                                    <TableData display={inmate.cashBalance != null} text={`£${inmate.cashBalance ? inmate.cashBalance.toFixed(2) : "0"}`} classes="hidden-col-lv-3" />

                                    <!-- Remand Indicator -->
                                    <TableData display={inmate.indicatorRemand} icon={"hourglass-split"} />

                                    <!-- Status -->
                                    <TableData display={inmate.status} text={inmate.status} tooltipId={`status-data-${inmate.inmateUrn}`}
                                        tooltipText={getCircumstanceDateTooltip(inmate.statusStartDate, inmate.statusFinishDate)}
                                        highlight={controlShowRecentChanges && inmate.isRecentStatusChange} classes={"hidden-col-lv-2"} />

                                    <!-- Keyworker -->
                                    {#if keyworkerExists}
                                        <TableData display={inmate.keyworkerName} text={inmate.keyworkerName} tooltipId={`keyworker-data-${inmate.inmateUrn}`}
                                            tooltipText={getCircumstanceDateTooltip(inmate.keyworkerStartDate, inmate.keyworkerFinishDate)} 
                                            highlight={controlShowRecentChanges && inmate.isRecentKeyworkerChange} />
                                    {/if}

                                    <!-- Site -->
                                    {#if $config.showSiteName && sites.length > 1}
                                        <TableData display={inmate.siteName} text={inmate.siteName} classes="hidden-col-lv-1" />
                                    {/if}

                                    <!-- Contact -->
                                    <TableAction display={$config.contactTypeId && requestProperties && requestProperties.requestDate && isToday(getIsoDateStringAsDate(requestProperties.requestDate))}
                                        icon={"sticky"} displayFilled={inmate.contactId} alertIcon={inmate.contactOverdue ? "clock" : "exclamation-circle"} displayAlert={inmate.contactWarning} 
                                        tooltipText={getContactWarningTooltip(inmate.contactId, inmate.contactTargetedUnseenDays, inmate.contactActionOverdueDays, inmate.contactIndicatorTargetedTotalIncreased, inmate.contactIndicatorIsolationIncreased)}
                                        tooltipOptions={getTooltipDisplayOptions({ position: "left" })}
                                        on:click={() => {openModal(inmate, inmate.contactId ? ModalTypeEnum.CONTACT : ModalTypeEnum.ADD_CONTACT)}} />

                                    <!-- Survey Schedule -->
                                    <TableAction display={$config.surveyUrn && requestProperties && requestProperties.requestDate && isToday(getIsoDateStringAsDate(requestProperties.requestDate))}
                                        icon={"clipboard-data"} displayFilled={inmate.surveyScheduleUrn} alertIcon={"clock"} displayAlert={inmate.surveyOverdue}
                                        tooltipText={getSurveyScheduleTooltip(inmate)} tooltipOptions={getTooltipDisplayOptions({ position: "left" })}
                                        on:click={() => {openModal(inmate, ModalTypeEnum.SURVEY)}} />
                                </tr>
                            {/each}
                        </tbody>
                    </table>

                    {#if selectedInmate}
                        {#if selectedModalType === ModalTypeEnum.SURVEY}
                            <SurveyModal {...selectedInmate} on:close={closeModal} />
                        {:else if selectedModalType === ModalTypeEnum.ADD_CONTACT}
                            <AddContactModal {...selectedInmate} on:close={closeModal} />
                        {:else if selectedModalType === ModalTypeEnum.CONTACT}
                            <ContactModal {...selectedInmate} on:close={closeModal} />
                        {/if}
                    {/if}
                {:else}
                    {#if ($inmateCount.filtered > $inmateCount.unfiltered) || ($inmateCount.unfiltered == 0 && isToday(getIsoDateStringAsDate(requestProperties.requestDate)))}
                        <div class="alert alert-warning" role="alert">
                            Data is being regenerated. Please wait a few moments and
                            <button type="button" class="btn btn-link refresh" on:click={() => dispatch("refresh")}>
                                Refresh
                            </button>
                        </div>
                    {:else if $inmateCount.unfiltered > 0}
                        <div class="alert alert-warning" role="alert">No {$config.inmateLabelName.toLowerCase()}s are currently being displayed. Please check the selected filters.</div>
                    {:else}
                        <div class="alert alert-danger" role="alert">No {$config.earlyDaysLabelName} Engagement records were found for the selected date. Please try selecting a different date.</div>
                    {/if}
                {/if}
            {:else}
                <div class="spinner-container">
                    <img id="aim-icon" src="/assets/logo-aim-target.png" alt="AIM Logo" />
                    <img id="aim-spinner" src="/assets/logo-radar-spinner.png" alt="AIM Logo" />
                </div>
            {/if}
        </div>
    </div>
</div>

<AimToast {...toastProperties}></AimToast>